/**
 * Icons — Ocean Blue Design System
 *
 * Central icon library. Import ALL icons from here:
 *   import { SearchIcon, FaEye, PlusIcon } from "../components/ui/Icons";
 *
 * Two types exported:
 *   1. Custom SVG icons — thin wrappers, consistent design
 *   2. react-icons re-exports — same import path, no per-file library imports
 *
 * Usage:
 *   <SearchIcon className="w-4 h-4 text-slate-400" />
 *   <FaEye size={14} />
 */

import React from "react";

// ─────────────────────────────────────────────────────────
// 1. react-icons re-exports (wrap for consistent defaults)
// ─────────────────────────────────────────────────────────

// fa (Font Awesome 4/5)
export { FaEye, FaEyeSlash, FaPen, FaTrash, FaSearch, FaUserCircle, FaCalendarAlt,
         FaFileAlt, FaFileExcel, FaPrint, FaFilePdf, FaUpload,
         FaChevronUp, FaChevronDown } from "react-icons/fa";

// fa6 (Font Awesome 6)
export { FaArrowRotateLeft } from "react-icons/fa6";

// bs (Bootstrap Icons)
export { BsDownload, BsPrinter, BsFileEarmarkText,
         BsFileEarmarkRuled, BsFileEarmarkCheck } from "react-icons/bs";

// ri (Remix Icons)
export { RiLogoutCircleRLine } from "react-icons/ri";

// rx (Radix Icons)
export { RxCross2 } from "react-icons/rx";

// ti (Themify Icons)
export { TiTick } from "react-icons/ti";


// ─────────────────────────────────────────────────────────
// 2. Custom SVG Icons
//    Props: className (string) — pass Tailwind size/color
//    Default: w-4 h-4, inherits currentColor
// ─────────────────────────────────────────────────────────

const svgBase = (path, viewBox = "0 0 24 24") =>
  ({ className = "w-4 h-4", strokeWidth = 2, ...rest }) => (
    <svg
      className={className}
      fill="none"
      viewBox={viewBox}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {path}
    </svg>
  );

// Navigation
export const ChevronLeft  = svgBase(<path d="M15 18l-6-6 6-6" />);
export const ChevronRight = svgBase(<path d="M9 18l6-6-6-6" />);
export const ChevronUp    = svgBase(<path d="M18 15l-6-6-6 6" />);
export const ChevronDown  = svgBase(<path d="M6 9l6 6 6-6" />);

export const ChevronDoubleLeft = svgBase(
  <><path d="M11 19l-7-7 7-7" /><path d="M18 19l-7-7 7-7" /></>
);
export const ChevronDoubleRight = svgBase(
  <><path d="M13 5l7 7-7 7" /><path d="M6 5l7 7-7 7" /></>
);

// Actions
export const PlusIcon = ({ className = "w-4 h-4", ...rest }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const SearchIcon = svgBase(
  <><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></>
);

export const FilterIcon = svgBase(
  <path d="M3 4h18M7 10h10M11 16h2" />
);

export const EditIcon = svgBase(
  <><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></>
);

export const DeleteIcon = svgBase(
  <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" /></>
);

export const ViewIcon = svgBase(
  <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
);

export const DownloadIcon = svgBase(
  <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>
);

export const UploadIcon = svgBase(
  <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>
);

export const PrintIcon = svgBase(
  <><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>
);

export const RefreshIcon = svgBase(
  <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></>
);

export const CloseIcon = svgBase(
  <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
);

export const CheckIcon = svgBase(
  <polyline points="20 6 9 17 4 12" />
);

export const AlertIcon = svgBase(
  <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>
);

// User / Auth
export const UserIcon = svgBase(
  <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>
);

export const LogoutIcon = svgBase(
  <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>
);

// Data / Finance
export const CalendarIcon = svgBase(
  <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>
);

export const FileIcon = svgBase(
  <><path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" /><polyline points="13 2 13 9 20 9" /></>
);

export const DashboardIcon = svgBase(
  <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>
);

export const ReportIcon = svgBase(
  <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></>
);

export const SettingsIcon = svgBase(
  <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></>
);
